import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Typography, Stack, Chip, Alert, Button,
  Select, MenuItem, Checkbox, FormControlLabel, Box, Collapse, IconButton,
  CircularProgress, Divider, List, ListItem, ListItemText, TextField,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Assignment as PrepIcon,
  Info as InfoIcon,
  Description as TranscriptIcon,
  Add as AddIcon,
  AutoAwesome as AnalyseIcon,
  CheckCircle as AcceptIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { calendarApi, projectsApi, transcriptsApi, workItemsApi } from '../api/services';
import type { CalendarEvent, AnnualStrategicDriver, Project, MeetingPrepResponse, MeetingTranscript, TranscriptExtraction } from '../types/models';

interface CalendarEventDetailProps {
  event: CalendarEvent | null;
  open: boolean;
  onClose: () => void;
  onDriverLinked: () => void;
  drivers: AnnualStrategicDriver[];
}

function formatEventTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function getBorderColor(event: CalendarEvent): string {
  if (event.isPriorityMeeting) return 'error.main';
  if (event.isPrivate) return 'warning.main';
  return 'primary.main';
}

export default function CalendarEventDetail({
  event,
  open,
  onClose,
  onDriverLinked,
  drivers,
}: CalendarEventDetailProps) {
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [applyToRecurring, setApplyToRecurring] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [activeProjects, setActiveProjects] = useState<Project[]>([]);
  // Meeting prep state
  const [prepData, setPrepData] = useState<MeetingPrepResponse | null>(null);
  const [prepLoading, setPrepLoading] = useState(false);
  const [prepExpanded, setPrepExpanded] = useState(false);
  const [prepError, setPrepError] = useState<string | null>(null);
  // Transcript state
  const [transcriptText, setTranscriptText] = useState('');
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const [transcriptUploading, setTranscriptUploading] = useState(false);
  const [transcript, setTranscript] = useState<MeetingTranscript | null>(null);
  const [transcriptFeedback, setTranscriptFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Quick action item state
  const [actionTitle, setActionTitle] = useState('');
  const [actionExpanded, setActionExpanded] = useState(false);
  const [actionCreating, setActionCreating] = useState(false);

  // Load active projects once
  React.useEffect(() => {
    projectsApi.getAll({ status: 'Active' }).then(setActiveProjects).catch(() => {});
  }, []);

  // Reset state when event changes
  React.useEffect(() => {
    if (event) {
      setSelectedDriverId(event.strategicDriverId || '');
      setSelectedProjectId(event.projectId || '');
      setApplyToRecurring(false);
      setFeedback(null);
      setDescriptionExpanded(false);
      setPrepData(null);
      setPrepExpanded(false);
      setPrepError(null);
      setTranscriptText('');
      setTranscriptExpanded(false);
      setTranscript(null);
      setTranscriptFeedback(null);
      setActionTitle('');
      setActionExpanded(false);
    }
  }, [event]);

  const handleDriverChange = async (driverId: string) => {
    if (!event) return;
    setSelectedDriverId(driverId);
    setSaving(true);
    setFeedback(null);
    try {
      await calendarApi.linkToDriver(event.id, {
        strategicDriverId: driverId || null,
        applyToRecurring,
      });
      setFeedback({ type: 'success', text: 'Strategic driver updated successfully' });
      onDriverLinked();
    } catch (err: any) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to link driver';
      setFeedback({
        type: 'error',
        text: typeof msg === 'string' ? msg : 'Failed to link driver',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleProjectChange = async (projectId: string) => {
    if (!event) return;
    setSelectedProjectId(projectId);
    setSaving(true);
    setFeedback(null);
    try {
      await calendarApi.linkToProject(event.id, {
        projectId: projectId || null,
      });
      setFeedback({ type: 'success', text: 'Project updated successfully' });
      onDriverLinked(); // reuse callback to refresh parent
    } catch (err: any) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to link project';
      setFeedback({
        type: 'error',
        text: typeof msg === 'string' ? msg : 'Failed to link project',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLoadMeetingPrep = async () => {
    if (!event) return;
    setPrepLoading(true);
    setPrepError(null);
    try {
      const data = await calendarApi.getMeetingPrep(event.id);
      setPrepData(data);
      setPrepExpanded(true);
    } catch (err: any) {
      setPrepError(err.message || 'Failed to load meeting prep');
    } finally {
      setPrepLoading(false);
    }
  };

  const handleUploadAndAnalyse = async () => {
    if (!event || !transcriptText.trim()) return;
    setTranscriptUploading(true);
    setTranscriptFeedback(null);
    try {
      const meetingDate = format(new Date(event.startTime), 'yyyy-MM-dd');
      const uploaded = await transcriptsApi.upload({
        title: event.title,
        meetingDate,
        attendees: event.attendees || undefined,
        transcriptText: transcriptText.trim(),
        calendarEventId: event.id,
      });
      const result = await transcriptsApi.autoAccept(uploaded.id);
      setTranscript(result.transcript);
      setTranscriptText('');
      setTranscriptFeedback({
        type: 'success',
        text: `Analysed — ${result.workItemsCreated} action item${result.workItemsCreated !== 1 ? 's' : ''} created`,
      });
    } catch (err: any) {
      setTranscriptFeedback({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to process transcript' });
    } finally {
      setTranscriptUploading(false);
    }
  };

  const handleCreateAction = async () => {
    if (!event || !actionTitle.trim()) return;
    setActionCreating(true);
    try {
      const meetingDate = format(new Date(event.startTime), 'yyyy-MM-dd');
      await workItemsApi.create({
        title: actionTitle.trim(),
        type: 'Task' as any,
        priority: 'High' as any,
        status: 'Backlog' as any,
        estimatedMinutes: 30,
        targetDate: meetingDate,
        strategicDriverId: event.strategicDriverId || undefined,
        notes: `From meeting: ${event.title}`,
      });
      setActionTitle('');
      setTranscriptFeedback({ type: 'success', text: 'Action item created' });
    } catch (err: any) {
      setTranscriptFeedback({ type: 'error', text: err.message || 'Failed to create action' });
    } finally {
      setActionCreating(false);
    }
  };

  if (!event) return null;

  const isIcsSource = !!event.icsSource;
  const currentDriver = drivers.find((d) => d.id === event.strategicDriverId);
  const currentProject = activeProjects.find((p) => p.id === event.projectId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          borderLeft: 4,
          borderColor: getBorderColor(event),
          ml: 0,
        }}
      >
        {event.title}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {/* Time */}
          <Stack direction="row" spacing={1} alignItems="center">
            <ScheduleIcon fontSize="small" color="action" />
            <Typography variant="body2">
              {event.isAllDay
                ? 'All day'
                : `${formatEventTime(event.startTime)} - ${formatEventTime(event.endTime)}`}
            </Typography>
          </Stack>

          {/* Location */}
          {event.location && (
            <Stack direction="row" spacing={1} alignItems="center">
              <LocationIcon fontSize="small" color="action" />
              <Typography variant="body2">{event.location}</Typography>
            </Stack>
          )}

          {/* Attendees */}
          {event.attendees && (
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <PeopleIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {event.attendees}
              </Typography>
            </Stack>
          )}

          {/* Description (collapsible) */}
          {event.description && (
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                sx={{ cursor: 'pointer', userSelect: 'none' }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Description
                </Typography>
                <IconButton size="small">
                  {descriptionExpanded ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              </Stack>
              <Collapse in={descriptionExpanded}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, whiteSpace: 'pre-line' }}
                >
                  {event.description}
                </Typography>
              </Collapse>
            </Box>
          )}

          {/* Strategic Driver section */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Strategic Driver
            </Typography>

            {currentDriver ? (
              <Chip
                label={currentDriver.title}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mb: 1.5 }}
              />
            ) : (
              <Chip
                label="No driver linked"
                size="small"
                variant="outlined"
                sx={{ mb: 1.5, color: 'text.secondary' }}
              />
            )}

            <Select
              value={selectedDriverId}
              onChange={(e) => handleDriverChange(e.target.value)}
              fullWidth
              size="small"
              displayEmpty
              disabled={saving}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {drivers.map((driver) => (
                <MenuItem key={driver.id} value={driver.id}>
                  {driver.title}
                </MenuItem>
              ))}
            </Select>

            <FormControlLabel
              control={
                <Checkbox
                  checked={applyToRecurring}
                  onChange={(e) => setApplyToRecurring(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Apply to all recurring instances
                </Typography>
              }
              sx={{ mt: 1 }}
            />

            {isIcsSource && (
              <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 1 }}>
                <InfoIcon sx={{ fontSize: 16, color: 'info.main', mt: 0.25 }} />
                <Typography variant="caption" color="text.secondary">
                  Link saved in Cadence. ICS calendars are read-only — the link won't appear in your external calendar.
                </Typography>
              </Stack>
            )}
          </Box>

          {/* Project section */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Project
            </Typography>

            {currentProject ? (
              <Chip
                label={currentProject.title}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mb: 1.5 }}
              />
            ) : (
              <Chip
                label="No project linked"
                size="small"
                variant="outlined"
                sx={{ mb: 1.5, color: 'text.secondary' }}
              />
            )}

            <Select
              value={selectedProjectId}
              onChange={(e) => handleProjectChange(e.target.value)}
              fullWidth
              size="small"
              displayEmpty
              disabled={saving}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {activeProjects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.title}
                </MenuItem>
              ))}
            </Select>

            {isIcsSource && (
              <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 1 }}>
                <InfoIcon sx={{ fontSize: 16, color: 'info.main', mt: 0.25 }} />
                <Typography variant="caption" color="text.secondary">
                  Link saved in Cadence. ICS calendars are read-only — the link won't appear in your external calendar.
                </Typography>
              </Stack>
            )}
          </Box>

          {/* Meeting Prep */}
          <Divider />
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={prepLoading ? <CircularProgress size={16} /> : <PrepIcon />}
                onClick={handleLoadMeetingPrep}
                disabled={prepLoading}
              >
                {prepData ? 'Refresh Meeting Prep' : 'Meeting Prep'}
              </Button>
              {prepData && (
                <IconButton size="small" onClick={() => setPrepExpanded(!prepExpanded)}>
                  {prepExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              )}
            </Stack>

            {prepError && (
              <Alert severity="error" sx={{ mt: 1 }} onClose={() => setPrepError(null)}>
                {prepError}
              </Alert>
            )}

            <Collapse in={prepExpanded && !!prepData}>
              {prepData && (
                <Stack spacing={2} sx={{ mt: 2 }}>
                  {/* Related Work Items */}
                  {prepData.relatedWorkItems.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Related Work Items ({prepData.relatedWorkItems.length})
                      </Typography>
                      <List dense disablePadding>
                        {prepData.relatedWorkItems.map((wi) => (
                          <ListItem key={wi.id} disableGutters sx={{ py: 0.25 }}>
                            <ListItemText
                              primary={wi.title}
                              secondary={`${wi.priority} | ${wi.status} | ${wi.estimatedMinutes}m`}
                              primaryTypographyProps={{ variant: 'body2' }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Previous Occurrences */}
                  {prepData.previousOccurrences.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Previous Meetings ({prepData.previousOccurrences.length})
                      </Typography>
                      <List dense disablePadding>
                        {prepData.previousOccurrences.map((pe) => (
                          <ListItem key={pe.id} disableGutters sx={{ py: 0.25 }}>
                            <ListItemText
                              primary={new Date(pe.startTime).toLocaleDateString()}
                              secondary={`${formatEventTime(pe.startTime)} - ${formatEventTime(pe.endTime)}`}
                              primaryTypographyProps={{ variant: 'body2' }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Recent Transcripts */}
                  {prepData.recentTranscripts.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Recent Transcripts ({prepData.recentTranscripts.length})
                      </Typography>
                      <List dense disablePadding>
                        {prepData.recentTranscripts.map((t) => (
                          <ListItem key={t.id} disableGutters sx={{ py: 0.25 }}>
                            <ListItemText
                              primary={t.title}
                              secondary={`${t.meetingDate}${t.aiSummary ? ` — ${t.aiSummary.substring(0, 100)}...` : ''}`}
                              primaryTypographyProps={{ variant: 'body2' }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Project Context */}
                  {prepData.projectStatus && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Project: {prepData.projectStatus.title}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip label={prepData.projectStatus.status} size="small" variant="outlined" />
                        <Chip label={`${prepData.projectStatus.progressPercentage}%`} size="small" color="primary" variant="outlined" />
                        <Chip label={`RAG: ${prepData.projectStatus.ragStatus}`} size="small"
                          color={prepData.projectStatus.ragStatus === 'Green' ? 'success' : prepData.projectStatus.ragStatus === 'Amber' ? 'warning' : 'error'}
                          variant="outlined"
                        />
                      </Stack>
                    </Box>
                  )}

                  {/* Driver KPI Summary */}
                  {prepData.driverSummary && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Strategic Driver: {prepData.driverSummary.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Progress: {prepData.driverSummary.progressPercentage}%
                      </Typography>
                      {prepData.driverSummary.kpis.length > 0 && (
                        <List dense disablePadding>
                          {prepData.driverSummary.kpis.map((kpi, i) => (
                            <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                              <ListItemText
                                primary={`${kpi.name}: ${kpi.currentValue ?? '—'}${kpi.unit ? ` ${kpi.unit}` : ''}`}
                                secondary={kpi.targetValue != null ? `Target: ${kpi.targetValue}${kpi.unit ? ` ${kpi.unit}` : ''}` : undefined}
                                primaryTypographyProps={{ variant: 'body2' }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Box>
                  )}

                  {/* Empty state */}
                  {prepData.relatedWorkItems.length === 0
                    && prepData.previousOccurrences.length === 0
                    && prepData.recentTranscripts.length === 0
                    && !prepData.projectStatus
                    && !prepData.driverSummary && (
                    <Typography variant="body2" color="text.secondary">
                      No prep context found for this meeting. Try linking a project or strategic driver.
                    </Typography>
                  )}
                </Stack>
              )}
            </Collapse>
          </Box>

          {/* Transcript Upload */}
          <Divider />
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              onClick={() => setTranscriptExpanded(!transcriptExpanded)}
              sx={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <TranscriptIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" color="text.secondary">
                Add Transcript
              </Typography>
              <IconButton size="small">
                {transcriptExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Stack>
            <Collapse in={transcriptExpanded}>
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                <TextField
                  multiline
                  minRows={3}
                  maxRows={8}
                  fullWidth
                  size="small"
                  placeholder="Paste meeting transcript or notes here..."
                  value={transcriptText}
                  onChange={(e) => setTranscriptText(e.target.value)}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={transcriptUploading ? <CircularProgress size={16} color="inherit" /> : <AnalyseIcon />}
                  onClick={handleUploadAndAnalyse}
                  disabled={transcriptUploading || !transcriptText.trim()}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {transcriptUploading ? 'Analysing...' : 'Upload & Extract Actions'}
                </Button>
                {transcript?.aiSummary && (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    <Typography variant="caption" fontWeight={600}>AI Summary</Typography>
                    <Typography variant="body2">{transcript.aiSummary}</Typography>
                  </Alert>
                )}
                {transcript?.extractions && transcript.extractions.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Extracted Actions ({transcript.extractions.length})
                    </Typography>
                    <List dense disablePadding>
                      {transcript.extractions.map((ext: TranscriptExtraction) => (
                        <ListItem key={ext.id} disableGutters sx={{ py: 0.25 }}>
                          <AcceptIcon sx={{ fontSize: 16, color: 'success.main', mr: 1 }} />
                          <ListItemText
                            primary={ext.title}
                            secondary={`${ext.priority} ${ext.workItemType} · ${ext.suggestedDurationMinutes}m`}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Stack>
            </Collapse>
          </Box>

          {/* Quick Action Item */}
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              onClick={() => setActionExpanded(!actionExpanded)}
              sx={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <AddIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" color="text.secondary">
                Quick Action Item
              </Typography>
              <IconButton size="small">
                {actionExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Stack>
            <Collapse in={actionExpanded}>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Action item title..."
                  value={actionTitle}
                  onChange={(e) => setActionTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && actionTitle.trim()) handleCreateAction(); }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={actionCreating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                  onClick={handleCreateAction}
                  disabled={actionCreating || !actionTitle.trim()}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Add
                </Button>
              </Stack>
            </Collapse>
          </Box>

          {/* Feedback */}
          {(feedback || transcriptFeedback) && (
            <Alert
              severity={(transcriptFeedback || feedback)!.type}
              onClose={() => { setFeedback(null); setTranscriptFeedback(null); }}
            >
              {(transcriptFeedback || feedback)!.text}
            </Alert>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
